import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { StatusPill, PriorityPill, RiskPill, FindingsStatusPill, InlineTabs, EmptyState, FingerprintChip } from "../components/UI";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import {
  ArrowLeft, Paperclip, EyeOff, Sparkles, MessageSquare, Send,
  Cpu, GitCompare, ShieldCheck, Download, Upload, AlertTriangle, Bot, Archive,
  CheckCircle2, XCircle, Fingerprint, User as UserIcon, X, Clock,
  Briefcase, MapPin, Search, Check, CheckCheck, Heart, Zap, TrendingUp,
  Wand2, RefreshCw,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Avatar from "../components/Avatar";

const STATUSES = ["open", "under_review", "investigation", "escalated", "resolved", "closed"];

export default function CaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("overview");
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [reporterProfileOpen, setReporterProfileOpen] = useState(false);
  const [hrList, setHrList] = useState([]);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [sentinel, setSentinel] = useState(false);
  const [dualPerspective, setDualPerspective] = useState(null);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [findingsModal, setFindingsModal] = useState(false);
  const [findingsOutcome, setFindingsOutcome] = useState("substantiated");
  const [findingsSummary, setFindingsSummary] = useState("");
  const [findingsActions, setFindingsActions] = useState("");
  const [findingsRejectReason, setFindingsRejectReason] = useState(null);
  const [evidenceReason, setEvidenceReason] = useState("");
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");
  const evidenceRef = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get(`/cases/${id}`);
      setC(data);
    } catch (e) { toast.error("Failed to load case"); nav("/cases"); }
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (["hr", "company_admin", "super_admin"].includes(user?.role)) {
      api.get("/users", { params: { role: "hr" } }).then((r) => setHrList(r.data)).catch(() => {});
    }
  }, [user?.role]);

  if (!c) return <div className="p-16 text-center text-slate-500 font-mono text-sm">Loading case…</div>;

  const canHR = ["hr", "company_admin", "super_admin"].includes(user.role);
  const canApproveFindings = ["company_admin", "super_admin"].includes(user.role);
  const canAddEvidence = ["manager", "hr", "company_admin", "super_admin"].includes(user.role);
  const canRequestArchive = canAddEvidence;

  const changeStatus = async (newStatus) => {
    try { const { data } = await api.patch(`/cases/${id}`, { status: newStatus }); setC(data); toast.success(`Status set to ${newStatus.replaceAll("_"," ")}`); }
    catch (e) { toast.error("Failed to update"); }
  };
  const assign = async (hrId) => {
    try { const { data } = await api.patch(`/cases/${id}`, { assigned_hr_id: hrId }); setC(data); toast.success("Assignee updated"); }
    catch (e) { toast.error("Failed to assign"); }
  };
  const uploadEvidence = async (files) => {
    if (!files || files.length === 0) return;
    if (!evidenceReason.trim()) { toast.error("Reason is required to upload evidence"); return; }
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("reason", evidenceReason);
      try {
        const { data } = await api.post(`/cases/${id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setC(data);
        toast.success(`Evidence added: ${file.name}`);
      } catch (e) { toast.error(`Upload failed for ${file.name}`); }
    }
    setEvidenceReason("");
  };

  const requestArchive = async () => {
    if (!archiveReason.trim()) return toast.error("Reason required");
    try {
      const { data } = await api.post(`/cases/${id}/evidence/${archiveTarget}/archive-request`, { reason: archiveReason });
      setC(data); setArchiveTarget(null); setArchiveReason("");
      toast.success("Archive request submitted , awaiting Company Admin approval");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try { const { data } = await api.post(`/cases/${id}/ai/reanalyze`); setC(data); toast.success("PropHR AI insight regenerated"); }
    catch (e) { toast.error("Reanalysis failed"); }
    finally { setReanalyzing(false); }
  };

  const runDualPerspective = async () => {
    setDualPerspective({ loading: true });
    try { const { data } = await api.post(`/cases/${id}/ai/dual-perspective`); setDualPerspective(data); }
    catch (e) { toast.error("Dual perspective failed"); setDualPerspective(null); }
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput;
    setChatMsgs((m) => [...m, { who: "you", text: msg }]);
    setChatInput("");
    setChatSending(true);
    try { const { data } = await api.post(`/cases/${id}/ai/chat`, { message: msg }); setChatMsgs((m) => [...m, { who: "ai", text: data.reply }]); }
    catch (e) { toast.error("Chat failed"); }
    finally { setChatSending(false); }
  };

  const submitFindings = async () => {
    try {
      const { data } = await api.post(`/cases/${id}/findings`, {
        outcome: findingsOutcome,
        decision_summary: findingsSummary,
        actions_taken: findingsActions.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      setC(data); setFindingsModal(false);
      toast.success(data.findings?.status === "approved" ? "Findings issued" : "Findings submitted for Company Admin approval");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to submit findings"); }
  };

  const approveFindings = async () => {
    try { const { data } = await api.post(`/cases/${id}/findings/approve`); setC(data); toast.success("Findings approved"); }
    catch (e) { toast.error("Failed"); }
  };
  const doRejectFindings = async () => {
    if (!findingsRejectReason?.trim()) return toast.error("Reason required");
    try { const { data } = await api.post(`/cases/${id}/findings/reject`, { reason: findingsRejectReason }); setC(data); setFindingsRejectReason(null); toast.success("Findings rejected"); }
    catch (e) { toast.error("Failed"); }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const resp = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const tabs = [
    { value: "overview", label: "Overview" },
    { value: "timeline", label: `Timeline (${c.timeline?.length || 0})` },
    { value: "evidence", label: `Evidence (${(c.evidence || []).filter(e => !e.is_archived).length})` },
    { value: "attachments", label: `Attachments (${c.attachments?.length || 0})` },
    { value: "ai", label: "PropHR AI Insights" },
    ...(canHR ? [{ value: "chat", label: "Case Assistant" }] : []),
    ...(c.workflow_state?.length > 0 ? [{ value: "workflow", label: `Workflow (${c.workflow_state.filter(s => s.status === "completed").length}/${c.workflow_state.length})` }] : []),
    { value: "findings", label: "Findings & Corrective Actions" },
  ];

  const insight = c.ai_insight || {};

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid={TID.caseDetail}>
      {/* Findings Stamp Ribbon , appears when findings have been submitted or finalized */}
      {(c.findings) && <FindingsRibbon findings={(c.findings)} onClickTab={() => setTab("findings")} />}

      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <Link to="/cases" className="text-xs text-slate-500 hover:text-slate-900 font-mono flex items-center gap-1 mb-2">
            <ArrowLeft size={12}/> ALL CASES
          </Link>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="font-mono text-xs text-slate-500">{c.case_number}</span>
            <StatusPill status={c.status} />
            <PriorityPill priority={c.priority} />
            <span className="pill pill-closed">{c.category}</span>
            {c.is_anonymous && <span className="pill pill-closed"><EyeOff size={10} strokeWidth={2}/> ANONYMOUS</span>}
          </div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight max-w-3xl">{c.title}</h1>
          <div className="text-sm text-slate-500 mt-2 flex items-center gap-4 flex-wrap font-mono text-xs">
            <span>Reporter: {c.reporter_name || "Anonymous"}</span>
            <span>•</span>
            <span>Created {new Date(c.created_at).toLocaleString()}</span>
            {c.sla_due_at && <><span>•</span><span>SLA due {new Date(c.sla_due_at).toLocaleString()}</span></>}
          </div>
        </div>

        {canHR && (
          <div className="flex flex-wrap gap-2">
            <select value={c.status} onChange={(e) => changeStatus(e.target.value)} data-testid={TID.caseChangeStatus} className="input !w-auto !py-1.5 !text-xs">
              {STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll("_"," ")}</option>)}
            </select>
            <select value={c.assigned_hr_id || ""} onChange={(e) => assign(e.target.value)} data-testid={TID.caseAssignHr} className="input !w-auto !py-1.5 !text-xs">
              <option value="">Assign HR…</option>
              {hrList.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
            {!(c.findings) && (
              <button className="btn-primary text-xs" onClick={() => setFindingsModal(true)} data-testid={TID.caseFindingsBtn}>
                <ShieldCheck size={14} strokeWidth={2}/> Submit findings
              </button>
            )}
          </div>
        )}
      </div>

      <InlineTabs tabs={tabs} value={tab} onChange={setTab} />

      <div className="mt-6 grid xl:grid-cols-4 lg:grid-cols-3 gap-6">
        {/* Main column (75%) , tabs + content */}
        <div className="xl:col-span-3 lg:col-span-2 space-y-6">
          {tab === "overview" && (
            <>
              <div className="data-cell p-6">
                <div className="eyebrow mb-3">Description</div>
                <div className="text-slate-800 leading-relaxed whitespace-pre-wrap text-sm">{c.description}</div>
              </div>
              {(c.involved_parties?.length > 0 || c.tags?.length > 0) && (
                <div className="grid md:grid-cols-2 gap-4">
                  {c.involved_parties?.length > 0 && (
                    <div className="data-cell p-4">
                      <div className="eyebrow mb-2">Involved Parties</div>
                      <div className="flex flex-wrap gap-2">
                        {c.involved_parties.map((p) => <span key={p} className="pill pill-closed">{p}</span>)}
                      </div>
                    </div>
                  )}
                  {c.tags?.length > 0 && (
                    <div className="data-cell p-4">
                      <div className="eyebrow mb-2">Tags</div>
                      <div className="flex flex-wrap gap-2">
                        {c.tags.map((t) => <span key={t} className="pill pill-medium">{t}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {tab === "timeline" && (
            <CommunicationStream c={c} user={user} onNewCase={setC} canHR={canHR}/>
          )}

          {tab === "evidence" && (
            <div className="data-cell p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="eyebrow flex items-center gap-2"><Fingerprint size={12} strokeWidth={2}/> Evidence Locker</div>
                {canAddEvidence && (
                  <div className="flex-1 max-w-md flex items-center gap-2">
                    <input value={evidenceReason} onChange={(e) => setEvidenceReason(e.target.value)}
                      placeholder="Reason for upload (required)…"
                      className="input !text-xs !py-1.5" data-testid={TID.caseEvidenceReason}/>
                    <input ref={evidenceRef} type="file" className="hidden" onChange={(e) => uploadEvidence(e.target.files)} data-testid={TID.caseUploadEvidenceInput}/>
                    <button className="btn-primary text-xs shrink-0" onClick={() => {
                      if (!evidenceReason.trim()) { toast.error("Reason is required"); return; }
                      evidenceRef.current?.click();
                    }} data-testid={TID.caseUploadEvidenceBtn}>
                      <Upload size={14} strokeWidth={2}/> Add
                    </button>
                  </div>
                )}
              </div>
              {(c.evidence || []).filter(e => !e.is_archived).length > 0 ? (
                <div className="space-y-2">
                  {c.evidence.filter(e => !e.is_archived).map((e) => (
                    <div key={e.id} className="border border-slate-200 bg-slate-50">
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Paperclip size={14} strokeWidth={2} className="text-slate-500"/>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{e.filename}</div>
                            <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                              {Math.round((e.size||0)/1024)} KB · uploaded by {e.uploaded_by_name || ","}
                            </div>
                            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                              <FingerprintChip fingerprint={e.fingerprint}/>
                              {e.reason && <span className="text-[10px] italic text-slate-600">&ldquo;{e.reason}&rdquo;</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button className="btn-ghost text-xs" onClick={() => downloadFile(e.id, e.filename)}>
                            <Download size={12} strokeWidth={2}/> Download
                          </button>
                          {canRequestArchive && (
                            <button className="text-[11px] text-slate-500 hover:text-red-700" onClick={() => setArchiveTarget(e.id)} title="Request archive (requires Company Admin approval)">
                              <Archive size={12} strokeWidth={2}/> Archive
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title="No evidence yet" subtitle="Managers and HR can upload evidence , every file requires a reason and is fingerprinted." icon={Upload} />
              )}

              {(c.evidence || []).filter(e => e.is_archived).length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="eyebrow mb-3 text-slate-400">Archived (visible to Admins)</div>
                  <div className="space-y-2">
                    {c.evidence.filter(e => e.is_archived).map((e) => (
                      <div key={e.id} className="border border-slate-200 bg-slate-100/50 px-4 py-3 opacity-70">
                        <div className="text-sm font-medium text-slate-700 truncate">{e.filename}</div>
                        <div className="text-[10px] font-mono text-slate-500 mt-0.5">Archived {new Date(e.archived_at).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "attachments" && (
            <div className="data-cell p-6">
              <div className="eyebrow mb-4">Reporter Attachments</div>
              {c.attachments?.length > 0 ? (
                <div className="space-y-2">
                  {c.attachments.map((a) => (
                    <div key={a.id} className="border border-slate-200 bg-slate-50">
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Paperclip size={14} strokeWidth={2} className="text-slate-500"/>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{a.filename}</div>
                            <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                              {a.kind?.toUpperCase()} · {Math.round((a.size||0)/1024)} KB
                              {a.transcription && " · transcribed"}
                            </div>
                            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                              <FingerprintChip fingerprint={a.fingerprint}/>
                              {a.reason && <span className="text-[10px] italic text-slate-600">&ldquo;{a.reason}&rdquo;</span>}
                            </div>
                          </div>
                        </div>
                        <button className="btn-ghost text-xs" onClick={() => downloadFile(a.id, a.filename)}>
                          <Download size={12} strokeWidth={2}/> Download
                        </button>
                      </div>
                      {a.transcription && (
                        <div className="border-t border-slate-200 px-4 py-3 bg-white">
                          <div className="eyebrow mb-1">Voice Transcription</div>
                          <div className="text-xs text-slate-700 italic">&ldquo;{a.transcription}&rdquo;</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title="No attachments" icon={Paperclip} />
              )}
            </div>
          )}

          {tab === "ai" && (
            <div className={"tracing-beam p-6 " + (sentinel ? "ai-surface" : "data-cell")}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Cpu size={16} strokeWidth={1.75} className={sentinel ? "text-blue-400" : "text-blue-700"}/>
                  <span className={"eyebrow " + (sentinel ? "text-blue-300" : "text-slate-500")}>
                    {sentinel ? "Sentinel Analytics · Hyper-Intelligent Synthesis" : "Sentinel Analytics"}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button className={"pill " + (sentinel ? "pill-medium" : "pill-closed")} onClick={() => setSentinel(!sentinel)} data-testid={TID.caseSentinelToggle}>
                    Sentinel Analytics {sentinel ? "ON" : "OFF"}
                  </button>
                  {canHR && (
                    <>
                      <button className="btn-ghost text-xs" onClick={reanalyze} disabled={reanalyzing} data-testid={TID.caseAIReanalyze}>
                        <Sparkles size={12} strokeWidth={2}/> {reanalyzing ? "Analyzing…" : "Re-analyze"}
                      </button>
                      <button className="btn-ghost text-xs" onClick={runDualPerspective} data-testid={TID.caseDualPerspective}>
                        <GitCompare size={12} strokeWidth={2}/> Dual Perspective
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className={"font-mono text-[11px] mb-4 " + (sentinel ? "text-slate-500" : "text-slate-400")}>
                POWERED BY SENTINEL ANALYTICS · GENERATED {insight.generated_at ? new Date(insight.generated_at).toLocaleString() : "·"} · CONFIDENCE {insight.confidence != null ? (insight.confidence * 100).toFixed(0) + "%" : "·"}
              </div>

              {/* Executive summary */}
              <div className={"font-editorial text-2xl leading-relaxed mb-4 " + (sentinel ? "text-slate-100" : "text-slate-900")}>
                {insight.summary || "Sentinel Analytics is analyzing this case in the background , insights will appear shortly."}
              </div>

              {/* Narrative brief */}
              {insight.narrative_brief && (
                <div className={"mb-6 pl-4 border-l-2 " + (sentinel ? "border-blue-400" : "border-blue-600")}>
                  <div className={"eyebrow mb-2 " + (sentinel ? "text-slate-500" : "")}>Narrative brief</div>
                  <div className={"text-sm leading-relaxed whitespace-pre-wrap " + (sentinel ? "text-slate-300" : "text-slate-700")}>
                    {insight.narrative_brief}
                  </div>
                </div>
              )}

              {/* Composite risk + sentiment */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className={"border p-4 " + (sentinel ? "border-slate-800" : "border-slate-200 bg-slate-50")}>
                  <div className={"eyebrow mb-2 " + (sentinel ? "text-slate-500" : "")}>Risk Level</div>
                  <div className="flex items-center gap-3">
                    <RiskPill level={insight.risk_level}/>
                    <span className={"font-display text-2xl font-semibold " + (sentinel ? "text-slate-100" : "text-slate-900")}>{insight.risk_score ?? "·"}<span className="text-xs opacity-60">/100</span></span>
                  </div>
                </div>
                <div className={"border p-4 " + (sentinel ? "border-slate-800" : "border-slate-200 bg-slate-50")}>
                  <div className={"eyebrow mb-2 " + (sentinel ? "text-slate-500" : "")}>Reporter Sentiment</div>
                  <div className={"font-display text-lg " + (sentinel ? "text-slate-100" : "text-slate-900")}>{insight.sentiment || "·"}</div>
                </div>
              </div>

              {/* Emotional signals */}
              {insight.emotional_signals?.length > 0 && (
                <div className="mb-6">
                  <div className={"eyebrow mb-3 flex items-center gap-2 " + (sentinel ? "text-slate-500" : "")}>
                    <Heart size={12} strokeWidth={2}/> Emotional signals
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {insight.emotional_signals.map((s, i) => (
                      <div key={i} className={"border p-3 " + (sentinel ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white")}>
                        <div className="flex items-center justify-between mb-1">
                          <div className={"text-sm font-semibold " + (sentinel ? "text-slate-100" : "text-slate-900")}>{s.label}</div>
                          <SignalPill level={s.level}/>
                        </div>
                        {s.evidence && (
                          <div className={"text-xs italic " + (sentinel ? "text-slate-400" : "text-slate-600")}>&ldquo;{s.evidence}&rdquo;</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk indicators */}
              {insight.risk_indicators?.length > 0 && (
                <div className="mb-6">
                  <div className={"eyebrow mb-3 flex items-center gap-2 " + (sentinel ? "text-slate-500" : "")}>
                    <AlertTriangle size={12} strokeWidth={2}/> Risk indicators
                  </div>
                  <div className="space-y-2">
                    {insight.risk_indicators.map((r, i) => (
                      <div key={i} className={"border p-3 " + (sentinel ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white")}>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <div className={"text-sm font-semibold " + (sentinel ? "text-slate-100" : "text-slate-900")}>{r.label}</div>
                          <SignalPill level={r.severity}/>
                        </div>
                        {r.rationale && (
                          <div className={"text-xs " + (sentinel ? "text-slate-400" : "text-slate-600")}>{r.rationale}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Data signals */}
              {insight.data_signals?.length > 0 && (
                <div className="mb-6">
                  <div className={"eyebrow mb-3 flex items-center gap-2 " + (sentinel ? "text-slate-500" : "")}>
                    <TrendingUp size={12} strokeWidth={2}/> Data signals
                  </div>
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {insight.data_signals.map((d, i) => (
                      <div key={i} className={"border p-3 " + (sentinel ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white")}>
                        <div className={"text-[10px] font-mono uppercase tracking-widest " + (sentinel ? "text-slate-500" : "text-slate-400")}>
                          {d.label}
                        </div>
                        <div className={"font-display text-xl font-semibold mt-1 " + (sentinel ? "text-slate-100" : "text-slate-900")}>
                          {d.value}
                        </div>
                        {d.note && (
                          <div className={"text-[11px] mt-1 " + (sentinel ? "text-slate-400" : "text-slate-600")}>{d.note}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline facts */}
              {insight.timeline_facts?.length > 0 && (
                <div className="mb-6">
                  <div className={"eyebrow mb-3 flex items-center gap-2 " + (sentinel ? "text-slate-500" : "")}>
                    <Clock size={12} strokeWidth={2}/> Timeline facts
                  </div>
                  <ol className={"space-y-1.5 pl-4 " + (sentinel ? "text-slate-300" : "text-slate-700")}>
                    {insight.timeline_facts.map((t, i) => (
                      <li key={i} className="text-sm relative">
                        <span className={"absolute -left-4 top-1.5 w-1.5 h-1.5 rounded-full " + (sentinel ? "bg-blue-400" : "bg-blue-600")}/>
                        {typeof t === "string" ? t : (t?.text || t?.label || JSON.stringify(t))}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {insight.key_points?.length > 0 && (
                <div className="mb-6">
                  <div className={"eyebrow mb-3 " + (sentinel ? "text-slate-500" : "")}>Key Points</div>
                  <ul className="space-y-2">
                    {insight.key_points.map((k, i) => (
                      <li key={i} className={"text-sm flex gap-2 " + (sentinel ? "text-slate-300" : "text-slate-700")}>
                        <span className={sentinel ? "text-blue-400" : "text-blue-700"}>›</span> {typeof k === "string" ? k : (k?.label || k?.text || JSON.stringify(k))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {insight.recommended_actions?.length > 0 && (
                <div className="mb-6">
                  <div className={"eyebrow mb-3 flex items-center gap-2 " + (sentinel ? "text-slate-500" : "")}>
                    <Zap size={12} strokeWidth={2}/> Recommended actions
                  </div>
                  <ol className="space-y-2">
                    {insight.recommended_actions.map((k, i) => (
                      <li key={i} className={"text-sm flex gap-3 " + (sentinel ? "text-slate-300" : "text-slate-700")}>
                        <span className={"font-mono text-xs w-5 h-5 flex items-center justify-center border shrink-0 mt-0.5 " + (sentinel ? "border-blue-400 text-blue-400" : "border-blue-600 text-blue-700")}>{i + 1}</span>
                        <span className="pt-0.5">{typeof k === "string" ? k : (k?.action || k?.label || k?.text || JSON.stringify(k))}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {insight.policy_references?.length > 0 && (
                <div>
                  <div className={"eyebrow mb-2 " + (sentinel ? "text-slate-500" : "")}>Policy References</div>
                  <div className="flex flex-wrap gap-2">
                    {insight.policy_references.map((p, i) => (
                      <span key={i} className={"pill pill-medium"}>{typeof p === "string" ? p : (p?.name || p?.label || JSON.stringify(p))}</span>
                    ))}
                  </div>
                </div>
              )}

              {dualPerspective && (
                <div className="mt-6 border border-slate-800 bg-slate-950 p-4">
                  <div className="eyebrow mb-3 text-blue-300">Dual Perspective Analysis</div>
                  {dualPerspective.loading ? (
                    <div className="text-slate-400 font-mono text-xs">PropHR AI running balanced adversarial reasoning…</div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4 text-xs">
                      <div>
                        <div className="text-slate-500 mb-1 font-mono uppercase tracking-wider">Reporter Perspective</div>
                        <div className="text-slate-200 leading-relaxed">{dualPerspective.reporter_perspective}</div>
                      </div>
                      <div>
                        <div className="text-slate-500 mb-1 font-mono uppercase tracking-wider">Subject Perspective</div>
                        <div className="text-slate-200 leading-relaxed">{dualPerspective.subject_perspective || dualPerspective.respondent_perspective}</div>
                      </div>
                      <div className="md:col-span-2 border-t border-slate-800 pt-3">
                        <div className="text-blue-300 mb-1 font-mono uppercase tracking-wider">Synthesis → {dualPerspective.recommended_outcome}</div>
                        <div className="text-slate-200 leading-relaxed">{dualPerspective.synthesis}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "chat" && canHR && (
            <div className="data-cell p-6">
              <div className="flex items-center gap-2 mb-4">
                <Bot size={16} strokeWidth={1.75} className="text-blue-700"/>
                <span className="eyebrow">PropHR AI · Case Assistant</span>
              </div>
              <div className="border border-slate-200 min-h-[280px] bg-slate-50 p-4 space-y-3 font-mono text-xs mb-3">
                {chatMsgs.length === 0 && (
                  <div className="text-slate-400 italic">Ask PropHR AI: &ldquo;What are the risks of this case?&rdquo;, &ldquo;Draft a response for the reporter&rdquo;, &ldquo;Which policies apply?&rdquo;</div>
                )}
                {chatMsgs.map((m, i) => (
                  <div key={i} className={m.who === "you" ? "text-slate-900" : "text-blue-700"}>
                    <span className="text-slate-500">{m.who === "you" ? ">" : "AI"}</span> {m.text}
                  </div>
                ))}
                {chatSending && <div className="text-slate-400 italic">Thinking…</div>}
              </div>
              <div className="flex gap-2">
                <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()}
                  placeholder="Ask PropHR AI about this case…" className="input" data-testid="case-chat-input"/>
                <button className="btn-primary" onClick={sendChat} disabled={chatSending} data-testid="case-chat-send">
                  <Send size={14} strokeWidth={2}/> Send
                </button>
              </div>
            </div>
          )}

          {tab === "workflow" && (
            <WorkflowPanel c={c} user={user} onChange={setC} />
          )}

          {tab === "findings" && (
            <div className="data-cell p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="eyebrow">Case Findings</div>
                {(c.findings)?.status && <FindingsStatusPill status={(c.findings).status}/>}
              </div>
              {(c.findings) ? (
                <div>
                  <div className="mb-4 flex items-center gap-2 flex-wrap">
                    <span className={"pill " + ((c.findings).outcome === "substantiated" ? "pill-critical" : (c.findings).outcome === "unsubstantiated" ? "pill-resolved" : "pill-medium")}>
                      {(c.findings).outcome.replaceAll("_"," ")}
                    </span>
                    <span className="text-xs text-slate-500 font-mono">
                      Submitted by {(c.findings).submitted_by_name} · {new Date((c.findings).submitted_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="font-editorial text-xl text-slate-900 mb-4 leading-relaxed">{(c.findings).decision_summary}</div>
                  {(c.findings).actions_taken?.length > 0 && (
                    <>
                      <div className="eyebrow mb-2">Actions Taken</div>
                      <ul className="space-y-1 mb-4">
                        {(c.findings).actions_taken.map((a, i) => (
                          <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-blue-700">✓</span> {a}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {(c.findings).fingerprint && (
                    <div className="mt-4">
                      <div className="eyebrow mb-1">Findings fingerprint</div>
                      <FingerprintChip fingerprint={(c.findings).fingerprint}/>
                    </div>
                  )}
                  {(c.findings).per_allegation?.length > 0 && (
                    <div className="mt-6 border-t border-slate-200 pt-4">
                      <div className="eyebrow mb-2">Per-allegation dispositions</div>
                      <div className="space-y-3">
                        {(c.findings).per_allegation.map((p) => (
                          <div key={p.allegation_tag} className="border border-slate-200 rounded p-3" data-testid={`findings-pa-${p.allegation_tag}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-mono text-xs bg-slate-100 rounded px-2 py-0.5">{p.allegation_tag}</span>
                              <span className={"text-[11px] font-mono uppercase tracking-widest " +
                                (p.disposition === "substantiated" ? "text-red-700" :
                                 p.disposition === "not_substantiated" ? "text-emerald-700" :
                                 p.disposition === "partially_substantiated" ? "text-amber-700" : "text-slate-600")}>
                                {p.disposition.replaceAll("_"," ")}
                              </span>
                            </div>
                            {p.investigative_summary && <div className="text-sm text-slate-800 mb-1">{p.investigative_summary}</div>}
                            {p.specific_finding_details && <div className="text-xs text-slate-600 italic">{p.specific_finding_details}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(c.findings).corrective_actions && (
                    <div className="mt-4 border-t border-slate-200 pt-4">
                      <div className="eyebrow mb-2">Corrective actions</div>
                      <div className="text-sm text-slate-800 space-y-1">
                        <div><span className="text-slate-500 font-mono uppercase tracking-wider text-xs">Discipline:</span> {(c.findings).corrective_actions.disciplinary_outcome.replaceAll("_"," ")}</div>
                        {(c.findings).corrective_actions.disciplinary_description && <div className="text-xs text-slate-600 italic">{(c.findings).corrective_actions.disciplinary_description}</div>}
                        {(c.findings).corrective_actions.development_requirements?.length > 0 && (
                          <div className="mt-1">
                            <div className="text-slate-500 font-mono uppercase tracking-wider text-xs">Development:</div>
                            <ul className="list-disc list-inside text-xs text-slate-700">
                              {(c.findings).corrective_actions.development_requirements.map((d, i) => <li key={i}>{d}</li>)}
                            </ul>
                          </div>
                        )}
                        {(c.findings).corrective_actions.process_changes && (
                          <div className="text-xs mt-1"><span className="text-slate-500 font-mono uppercase tracking-wider">Process changes:</span> {(c.findings).corrective_actions.process_changes}</div>
                        )}
                      </div>
                      {((c.findings).corrective_actions.monitoring_30_day ||
                        (c.findings).corrective_actions.monitoring_60_day ||
                        (c.findings).corrective_actions.monitoring_90_day) && (
                        <div className="mt-3 grid md:grid-cols-3 gap-2" data-testid="findings-monitoring-cadence">
                          {["30", "60", "90"].map((n) => {
                            const key = `monitoring_${n}_day`;
                            const v = (c.findings).corrective_actions[key];
                            if (!v) return null;
                            return (
                              <div key={n} className="border border-slate-200 rounded p-2">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{n} day</div>
                                <div className="text-xs text-slate-800">{v}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {(c.findings).closing_communications && (
                    <div className="mt-4 border-t border-slate-200 pt-4" data-testid="findings-closing-comms">
                      <div className="eyebrow mb-2">Closing communications</div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {["reporter", "subject", "witnesses"].map((who) => {
                          const key = `${who}_confirmed`;
                          const ok = (c.findings).closing_communications[key];
                          return (
                            <span key={who} className={"inline-flex items-center gap-1 px-2 py-0.5 rounded " +
                              (ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200")}>
                              {ok ? <CheckCircle2 size={11}/> : <Clock size={11}/>}
                              {who}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {(c.findings).status === "rejected" && (c.findings).rejection_reason && (
                    <div className="mt-4 border-l-4 border-red-600 bg-red-50 p-3">
                      <div className="text-xs font-mono text-red-900 uppercase tracking-wider mb-1">Rejected by {(c.findings).approved_by_name}</div>
                      <div className="text-sm text-red-900">{(c.findings).rejection_reason}</div>
                    </div>
                  )}
                  {(c.findings).status === "approved" && (c.findings).approved_by_name && (c.findings).approved_by !== (c.findings).submitted_by && (
                    <div className="mt-4 border-l-4 border-emerald-600 bg-emerald-50 p-3">
                      <div className="text-xs font-mono text-emerald-900 uppercase tracking-wider mb-1">Approved by {(c.findings).approved_by_name}</div>
                      <div className="text-xs text-emerald-900">{new Date((c.findings).approved_at).toLocaleString()}</div>
                    </div>
                  )}
                  {(c.findings).status === "pending_approval" && canApproveFindings && (
                    <div className="mt-4 border border-amber-300 bg-amber-50 p-4">
                      <div className="text-xs font-semibold text-amber-900 mb-2 uppercase tracking-wider">Awaiting your approval</div>
                      <div className="flex gap-2">
                        <button className="btn-primary" onClick={approveFindings} data-testid={TID.caseFindingsApprove}><CheckCircle2 size={14}/> Approve findings</button>
                        <button className="btn-danger" onClick={() => setFindingsRejectReason("")} data-testid={TID.caseFindingsReject}><XCircle size={14}/> Reject</button>
                      </div>
                    </div>
                  )}
                  {(c.findings).status === "pending_approval" && !canApproveFindings && (
                    <div className="mt-4 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                      These findings are awaiting Company Admin approval. It will not be finalized until approved.
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState
                  icon={ShieldCheck}
                  title="No findings submitted"
                  subtitle={canHR ? "HR can submit findings for approval. Company Admin has final sign-off." : "The HR team is still investigating this case."}
                  action={canHR ? <button className="btn-primary" onClick={() => setFindingsModal(true)} data-testid={TID.caseFindingsBtn}><ShieldCheck size={14}/> Submit findings</button> : null}
                />
              )}
            </div>
          )}
        </div>

        {/* Sidebar (25%) , sticky Workflow Widget + Embedded AI Companion. */}
        <div className="space-y-6 xl:sticky xl:top-20 xl:self-start xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto pr-1" data-testid="case-right-rail">
          {c.workflow_state?.length > 0 && (
            <WorkflowMiniCard c={c} onOpen={() => setTab("workflow")} />
          )}

          <AICompanion c={c} canHR={canHR} onReanalyze={reanalyze} reanalyzing={reanalyzing} />

          <div className="data-cell p-5">
            <div className="eyebrow mb-3">Case Metadata</div>
            <dl className="space-y-2 text-xs">
              <div className="flex justify-between items-center gap-2">
                <dt className="text-slate-500">Reporter</dt>
                <dd>
                  {c.is_anonymous && !canHR ? (
                    <span className="font-mono text-slate-500">Anonymous</span>
                  ) : c.reporter_id ? (
                    <button
                      onClick={() => setReporterProfileOpen(true)}
                      className="text-blue-700 hover:underline font-mono text-xs"
                      data-testid={TID.reporterProfileTrigger}
                    >
                      {c.reporter_name}
                    </button>
                  ) : (
                    <span className="font-mono">{c.reporter_name || "Anonymous"}</span>
                  )}
                </dd>
              </div>
              <div className="flex justify-between"><dt className="text-slate-500">Assignee</dt><dd className="font-mono">{c.assigned_hr_name || "Unassigned"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Reporter dept</dt><dd className="font-mono">{c.reporter_department || ","}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Created</dt><dd className="font-mono">{new Date(c.created_at).toLocaleDateString()}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Updated</dt><dd className="font-mono">{new Date(c.updated_at).toLocaleDateString()}</dd></div>
              {c.sla_due_at && <div className="flex justify-between"><dt className="text-slate-500">SLA due</dt><dd className="font-mono">{new Date(c.sla_due_at).toLocaleDateString()}</dd></div>}
              {c.resolved_at && <div className="flex justify-between"><dt className="text-slate-500">Resolved</dt><dd className="font-mono">{new Date(c.resolved_at).toLocaleDateString()}</dd></div>}
              {c.withdrawn_at && <div className="flex justify-between"><dt className="text-slate-500">Withdrawn</dt><dd className="font-mono">{new Date(c.withdrawn_at).toLocaleDateString()}</dd></div>}
            </dl>
          </div>

          {/* Withdraw case (reporter only, before it is closed/resolved/withdrawn) */}
          {c.reporter_id === user.id && !["withdrawn", "resolved", "closed"].includes(c.status) && (
            <button
              className="btn-ghost w-full !text-red-700 !border-red-200 hover:!bg-red-50"
              onClick={() => setWithdrawOpen(true)}
              data-testid={TID.caseWithdrawBtn}
            >
              <XCircle size={14} strokeWidth={2}/> Withdraw this case
            </button>
          )}

          <div className="border border-amber-300 bg-amber-50 p-4">
            <AlertTriangle size={16} className="text-amber-700 mb-2" strokeWidth={2}/>
            <div className="text-xs text-amber-900 leading-relaxed">
              This case contains confidential HR data. Do not share externally. PropHR AI recommendations are advisory only.
            </div>
          </div>
        </div>
      </div>

      {/* Findings Wizard , per-allegation dispositions + corrective actions + monitoring cadence */}
      {findingsModal && (
        <FindingsWizard
          case_={c}
          canApproveFindings={canApproveFindings}
          onClose={() => setFindingsModal(false)}
          onSubmitted={(newCase) => { setC(newCase); setFindingsModal(false); }}
        />
      )}

      {/* Reject findings */}
      {findingsRejectReason !== null && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-300 max-w-md w-full p-6 rounded">
            <h3 className="font-display text-xl font-semibold mb-3">Reject Findings</h3>
            <textarea rows={4} value={findingsRejectReason} onChange={(e) => setFindingsRejectReason(e.target.value)} placeholder="Reason for rejection…" className="input mb-4"/>
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setFindingsRejectReason(null)}>Cancel</button>
              <button className="btn-danger" onClick={doRejectFindings}>Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Evidence archive request */}
      {archiveTarget && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-300 max-w-md w-full p-6 rounded">
            <h3 className="font-display text-xl font-semibold mb-2">Request Evidence Archive</h3>
            <p className="text-sm text-slate-500 mb-3">Evidence is never deleted. Archival requires Company Admin approval and is fully audit-logged.</p>
            <textarea rows={4} value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} placeholder="Reason for archival…" className="input mb-4"/>
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => { setArchiveTarget(null); setArchiveReason(""); }}>Cancel</button>
              <button className="btn-primary" onClick={requestArchive}><Archive size={14}/> Submit request</button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw case */}
      <AnimatePresence>
        {withdrawOpen && (
          <WithdrawCaseModal
            caseId={c.id}
            onClose={() => setWithdrawOpen(false)}
            onWithdrawn={(newCase) => { setC(newCase); setWithdrawOpen(false); }}
          />
        )}
      </AnimatePresence>

      {/* Reporter profile drill-down */}
      <AnimatePresence>
        {reporterProfileOpen && c.reporter_id && (
          <ReporterProfileModal
            userId={c.reporter_id}
            onClose={() => setReporterProfileOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ============================================================================
   AI COMPANION , sticky right-rail assistant (Section 3.4.2).
   Compact widget with 4-action pattern: Use Draft / Edit / Regenerate / Dismiss.
   ============================================================================ */

function AICompanion({ c, canHR, onReanalyze, reanalyzing }) {
  const [dismissed, setDismissed] = useState(false);
  const insight = c.ai_insight || {};
  const nav = useNavigate();
  const draft = insight.summary || insight.narrative_brief;
  const risk = insight.risk_level;

  const useDraft = () => {
    if (!draft) return;
    navigator.clipboard?.writeText(draft).catch(() => {});
    toast.success("Draft copied to clipboard");
  };
  const editDraft = () => {
    nav(`/cases/${c.id}?tab=ai`);
  };

  if (dismissed) {
    return (
      <div className="data-cell p-3 flex items-center justify-between">
        <div className="text-xs text-slate-500 inline-flex items-center gap-2">
          <Cpu size={12} strokeWidth={2}/> AI Companion dismissed
        </div>
        <button className="text-xs text-blue-700 hover:underline" onClick={() => setDismissed(false)}>
          Bring it back
        </button>
      </div>
    );
  }

  return (
    <div className="data-cell p-4" data-testid="ai-companion">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-slate-900 text-white grid place-items-center">
            <Cpu size={12} strokeWidth={2}/>
          </div>
          <div className="eyebrow">AI Companion</div>
        </div>
        {risk && (
          <span className={"pill " + (risk === "critical" ? "pill-critical" : risk === "high" ? "pill-open" : "pill-closed")}>
            {risk} risk
          </span>
        )}
      </div>
      <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3 leading-snug">
        Advisory only. Final decisions remain with the assigned investigator.
      </div>

      {draft ? (
        <div className="text-sm text-slate-800 leading-snug bg-slate-50 border border-slate-200 rounded p-3 mb-3 max-h-[220px] overflow-y-auto">
          {draft}
        </div>
      ) : (
        <div className="text-xs text-slate-500 italic mb-3">
          No AI draft yet for this case. Click &ldquo;Regenerate&rdquo; to run a fresh analysis.
        </div>
      )}

      {canHR && (
        <div className="grid grid-cols-2 gap-2" data-testid="ai-companion-actions">
          <button className="btn-primary text-xs" onClick={useDraft} disabled={!draft} data-testid="ai-action-use-draft">
            <CheckCircle2 size={12} strokeWidth={2}/> Use draft
          </button>
          <button className="btn-ghost text-xs" onClick={editDraft} disabled={!draft} data-testid="ai-action-edit">
            <MessageSquare size={12} strokeWidth={2}/> Edit & refine
          </button>
          <button className="btn-ghost text-xs" onClick={onReanalyze} disabled={reanalyzing} data-testid="ai-action-regenerate">
            <Sparkles size={12} strokeWidth={2}/> {reanalyzing ? "Regenerating..." : "Regenerate"}
          </button>
          <button className="btn-ghost text-xs" onClick={() => setDismissed(true)} data-testid="ai-action-dismiss">
            <X size={12} strokeWidth={2}/> Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

/* ============================================================================
   FINDINGS WIZARD , per-allegation dispositions + corrective actions +
   30/60/90 monitoring + closing communications (Section 3.3D of FLY2619).
   ============================================================================ */

function FindingsWizard({ case_, canApproveFindings, onClose, onSubmitted }) {
  const c = case_;
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState("substantiated");
  const [summary, setSummary] = useState("");
  const [comprehensiveDecision, setComprehensiveDecision] = useState("");
  const [actions, setActions] = useState("");
  const [perAllegation, setPerAllegation] = useState(
    (c.allegation_tags || []).map((tag) => ({
      allegation_tag: tag,
      disposition: "substantiated",
      investigative_summary: "",
      specific_finding_details: "",
      ai_drafted: false,
    }))
  );
  const [evVerified, setEvVerified] = useState({
    interviews_conducted: false,
    documents_reviewed: false,
    timeline_validated: false,
    other: false,
    other_note: "",
  });
  const [ca, setCA] = useState({
    disciplinary_outcome: "none",
    disciplinary_description: "",
    development_requirements: "",
    process_changes: "",
    monitoring_30_day: "",
    monitoring_60_day: "",
    monitoring_90_day: "",
  });
  const [comms, setComms] = useState({
    reporter_confirmed: false,
    subject_confirmed: false,
    witnesses_confirmed: false,
  });
  const [closureDate, setClosureDate] = useState(new Date().toISOString().slice(0, 10));

  // Sprint 7 , Findings AI Assist state (advisory only, single-case scope).
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const [aiApplied, setAiApplied] = useState(false);

  const setPA = (i, patch) => setPerAllegation((prev) =>
    prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p))
  );

  const applyDraft = (draft) => {
    if (!draft?.per_allegation) return;
    const byTag = new Map(draft.per_allegation.map((r) => [r.allegation_tag, r]));
    setPerAllegation((prev) =>
      prev.map((p) => {
        const r = byTag.get(p.allegation_tag);
        if (!r) return p;
        return {
          ...p,
          disposition: r.disposition || p.disposition,
          investigative_summary: r.investigative_summary || p.investigative_summary,
          specific_finding_details: r.specific_finding_details || p.specific_finding_details,
          ai_drafted: true,
        };
      })
    );
    if (draft.overall_outcome) setOutcome(draft.overall_outcome);
    if (draft.decision_summary && !summary.trim()) setSummary(draft.decision_summary);
    if ((draft.recommended_actions || []).length && !actions.trim()) {
      setActions(draft.recommended_actions.join("\n"));
    }
    setAiApplied(true);
  };

  const runAiDraft = async ({ apply = true } = {}) => {
    if (busy || aiBusy) return;
    if (!perAllegation.length) {
      toast.error("Add allegation tags on the Overview tab before drafting with PropHR AI.");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post(`/cases/${c.id}/findings/ai-draft`);
      setAiDraft(data);
      if (apply) applyDraft(data);
      toast.success("PropHR AI draft ready. Review, edit, or regenerate.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally {
      setAiBusy(false);
    }
  };

  const dismissDraft = () => {
    setAiDraft(null);
    setAiApplied(false);
    setPerAllegation((prev) => prev.map((p) => ({ ...p, ai_drafted: false })));
    toast("PropHR AI draft dismissed. Your manual edits are preserved.");
  };

  const submit = async () => {
    if (!summary.trim()) { toast.error("Overall decision summary is required."); return; }
    setBusy(true);
    try {
      const payload = {
        outcome,
        decision_summary: summary.trim(),
        actions_taken: actions.split("\n").map((s) => s.trim()).filter(Boolean),
        per_allegation: perAllegation.map((p) => ({
          ...p,
          investigative_summary: p.investigative_summary?.trim() || null,
          specific_finding_details: p.specific_finding_details?.trim() || null,
        })),
        evidence_verified: evVerified,
        corrective_actions: {
          disciplinary_outcome: ca.disciplinary_outcome,
          disciplinary_description: ca.disciplinary_description?.trim() || null,
          development_requirements: ca.development_requirements.split("\n").map((s) => s.trim()).filter(Boolean),
          process_changes: ca.process_changes?.trim() || null,
          monitoring_30_day: ca.monitoring_30_day?.trim() || null,
          monitoring_60_day: ca.monitoring_60_day?.trim() || null,
          monitoring_90_day: ca.monitoring_90_day?.trim() || null,
        },
        closing_communications: {
          reporter_confirmed: comms.reporter_confirmed,
          subject_confirmed: comms.subject_confirmed,
          witnesses_confirmed: comms.witnesses_confirmed,
        },
        closure_date: closureDate,
        comprehensive_decision: comprehensiveDecision.trim() || null,
      };
      const { data } = await api.post(`/cases/${c.id}/findings`, payload);
      onSubmitted(data);
      toast.success(canApproveFindings ? "Findings issued" : "Submitted for Company Admin approval");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  const stepLabels = ["Per-allegation", "Overall & evidence", "Corrective actions", "Closing communications"];

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-300 max-w-3xl w-full rounded shadow-xl flex flex-col max-h-[92vh]" data-testid="findings-wizard">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-xl font-semibold">Findings and corrective actions</h3>
            <button className="text-slate-400 hover:text-slate-900" onClick={onClose} data-testid="findings-wizard-close"><X size={18}/></button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {stepLabels.map((label, i) => (
              <button key={label} onClick={() => setStep(i + 1)}
                className={"text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded " +
                  (step === i + 1 ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200")}
                data-testid={`findings-wizard-step-${i + 1}`}>
                {i + 1}. {label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {step === 1 && (
            <div className="space-y-5">
              <div className="text-sm text-slate-600">Set a disposition per allegation from the intake.</div>

              {/* PropHR AI Findings Assist panel , advisory only, per-case scope */}
              {perAllegation.length > 0 && (
                <div className="border border-slate-900 rounded bg-gradient-to-r from-slate-900 to-slate-800 text-slate-100 p-4" data-testid="findings-ai-assist">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-white/10 grid place-items-center shrink-0">
                        <Wand2 size={14} strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-300">PropHR AI Findings Assist</div>
                        <div className="text-sm text-slate-100 leading-snug">
                          Pre-fill each allegation with a first-pass disposition, summary, and evidence anchors.
                          {aiApplied && aiDraft && (
                            <span className="text-slate-300 text-xs ml-1">
                              (confidence {Math.round((aiDraft.confidence || 0) * 100)}%)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {!aiApplied && (
                      <button
                        type="button"
                        className="text-xs font-mono uppercase tracking-widest bg-white text-slate-900 hover:bg-slate-100 px-3 py-2 rounded inline-flex items-center gap-2 disabled:opacity-60"
                        onClick={() => runAiDraft({ apply: true })}
                        disabled={aiBusy}
                        data-testid="findings-ai-draft-btn"
                      >
                        <Sparkles size={12} strokeWidth={2} />
                        {aiBusy ? "Drafting…" : "Draft with PropHR AI"}
                      </button>
                    )}
                  </div>
                  <div className="text-[11px] text-amber-200/90 mt-3 leading-snug border-t border-white/10 pt-3">
                    Advisory only. PropHR AI drafts a suggestion for this case only, from this case&apos;s evidence and timeline.
                    Final findings remain the investigator&apos;s decision.
                  </div>
                  {aiApplied && aiDraft && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-testid="findings-ai-actions">
                      <button
                        type="button"
                        className="text-xs font-mono uppercase tracking-widest bg-white text-slate-900 hover:bg-slate-100 px-2 py-1.5 rounded inline-flex items-center justify-center gap-1 disabled:opacity-60"
                        onClick={() => applyDraft(aiDraft)}
                        data-testid="findings-ai-use-draft"
                      >
                        <CheckCircle2 size={12} strokeWidth={2} /> Use draft
                      </button>
                      <button
                        type="button"
                        className="text-xs font-mono uppercase tracking-widest bg-white/10 hover:bg-white/20 px-2 py-1.5 rounded inline-flex items-center justify-center gap-1"
                        onClick={() => toast("Edit any row below to refine the draft.")}
                        data-testid="findings-ai-edit"
                      >
                        <MessageSquare size={12} strokeWidth={2} /> Edit and refine
                      </button>
                      <button
                        type="button"
                        className="text-xs font-mono uppercase tracking-widest bg-white/10 hover:bg-white/20 px-2 py-1.5 rounded inline-flex items-center justify-center gap-1 disabled:opacity-60"
                        onClick={() => runAiDraft({ apply: true })}
                        disabled={aiBusy}
                        data-testid="findings-ai-regenerate"
                      >
                        <RefreshCw size={12} strokeWidth={2} className={aiBusy ? "animate-spin" : ""} />
                        {aiBusy ? "Regenerating…" : "Regenerate"}
                      </button>
                      <button
                        type="button"
                        className="text-xs font-mono uppercase tracking-widest bg-white/10 hover:bg-white/20 px-2 py-1.5 rounded inline-flex items-center justify-center gap-1"
                        onClick={dismissDraft}
                        data-testid="findings-ai-dismiss"
                      >
                        <X size={12} strokeWidth={2} /> Dismiss
                      </button>
                    </div>
                  )}
                </div>
              )}

              {perAllegation.length === 0 ? (
                <div className="text-sm text-slate-500 italic">This case has no allegation tags. Add tags on the Overview tab before recording per-allegation findings.</div>
              ) : (
                perAllegation.map((p, i) => (
                  <div key={p.allegation_tag} className="border border-slate-200 rounded p-4" data-testid={`per-allegation-row-${p.allegation_tag}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <div className="font-mono text-xs bg-slate-100 rounded px-2 py-0.5">{p.allegation_tag}</div>
                        {p.ai_drafted && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-slate-900 text-white px-1.5 py-0.5 rounded" data-testid={`ai-drafted-badge-${p.allegation_tag}`}>
                            <Sparkles size={9} strokeWidth={2} /> AI drafted
                          </span>
                        )}
                      </div>
                      <select
                        className="input !py-1 !w-auto !text-xs"
                        value={p.disposition}
                        onChange={(e) => setPA(i, { disposition: e.target.value, ai_drafted: false })}
                        data-testid={`disposition-${p.allegation_tag}`}
                      >
                        <option value="substantiated">Substantiated</option>
                        <option value="partially_substantiated">Partially substantiated</option>
                        <option value="not_substantiated">Not substantiated</option>
                        <option value="inconclusive">Inconclusive</option>
                      </select>
                    </div>
                    <label className="eyebrow block mb-1">Investigative summary</label>
                    <textarea rows={2} className="input mb-2"
                      value={p.investigative_summary}
                      onChange={(e) => setPA(i, { investigative_summary: e.target.value, ai_drafted: false })}
                      placeholder="What did the investigation reveal for this specific allegation?" />
                    <label className="eyebrow block mb-1">Specific finding details</label>
                    <textarea rows={2} className="input"
                      value={p.specific_finding_details}
                      onChange={(e) => setPA(i, { specific_finding_details: e.target.value, ai_drafted: false })}
                      placeholder="Facts, dates, evidence anchors that support this disposition." />
                  </div>
                ))
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="eyebrow block mb-1">Overall outcome</label>
                <select className="input" value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="findings-overall-outcome">
                  <option value="substantiated">Substantiated</option>
                  <option value="partially_substantiated">Partially substantiated</option>
                  <option value="unsubstantiated">Unsubstantiated</option>
                  <option value="inconclusive">Inconclusive</option>
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1">Decision summary <span className="text-red-600">*</span></label>
                <textarea rows={3} className="input" value={summary} onChange={(e) => setSummary(e.target.value)}
                  placeholder="Explain the overall decision and reasoning." data-testid="findings-summary" />
              </div>
              <div>
                <label className="eyebrow block mb-1">Comprehensive decision (optional)</label>
                <textarea rows={4} className="input" value={comprehensiveDecision} onChange={(e) => setComprehensiveDecision(e.target.value)}
                  placeholder="Long-form executive briefing suitable for the closing communication."/>
              </div>
              <div className="border border-slate-200 rounded p-3">
                <div className="eyebrow mb-2">Evidence verified</div>
                {[
                  ["interviews_conducted", "Interviews conducted"],
                  ["documents_reviewed", "Documents reviewed"],
                  ["timeline_validated", "Timeline validated"],
                  ["other", "Other"],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                    <input type="checkbox"
                      checked={evVerified[key]}
                      onChange={(e) => setEvVerified((v) => ({ ...v, [key]: e.target.checked }))}
                      data-testid={`ev-verified-${key}`}
                    />
                    {label}
                  </label>
                ))}
                {evVerified.other && (
                  <input className="input mt-2" placeholder="Note about other evidence"
                    value={evVerified.other_note}
                    onChange={(e) => setEvVerified((v) => ({ ...v, other_note: e.target.value }))} />
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="eyebrow block mb-1">Disciplinary outcome</label>
                <select className="input" value={ca.disciplinary_outcome}
                  onChange={(e) => setCA({ ...ca, disciplinary_outcome: e.target.value })} data-testid="ca-disciplinary">
                  <option value="none">None</option>
                  <option value="verbal_warning">Verbal warning</option>
                  <option value="written_warning">Written warning</option>
                  <option value="suspension">Suspension</option>
                  <option value="termination">Termination</option>
                  <option value="other">Other</option>
                </select>
                <textarea rows={2} className="input mt-2" placeholder="Optional description of the disciplinary action."
                  value={ca.disciplinary_description}
                  onChange={(e) => setCA({ ...ca, disciplinary_description: e.target.value })} />
              </div>
              <div>
                <label className="eyebrow block mb-1">Development requirements (one per line)</label>
                <textarea rows={3} className="input" placeholder="e.g. Anti-harassment training&#10;Manager coaching sessions"
                  value={ca.development_requirements}
                  onChange={(e) => setCA({ ...ca, development_requirements: e.target.value })} />
              </div>
              <div>
                <label className="eyebrow block mb-1">Process changes (optional)</label>
                <textarea rows={2} className="input" placeholder="Policy or process updates."
                  value={ca.process_changes}
                  onChange={(e) => setCA({ ...ca, process_changes: e.target.value })} />
              </div>
              <div className="border border-slate-200 rounded p-3">
                <div className="eyebrow mb-2">Monitoring cadence (30 / 60 / 90 day)</div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">30 day check-in</div>
                    <textarea rows={3} className="input" value={ca.monitoring_30_day}
                      onChange={(e) => setCA({ ...ca, monitoring_30_day: e.target.value })}
                      placeholder="What to verify after 30 days." data-testid="monitor-30" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">60 day check-in</div>
                    <textarea rows={3} className="input" value={ca.monitoring_60_day}
                      onChange={(e) => setCA({ ...ca, monitoring_60_day: e.target.value })}
                      placeholder="What to verify after 60 days." data-testid="monitor-60" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">90 day check-in</div>
                    <textarea rows={3} className="input" value={ca.monitoring_90_day}
                      onChange={(e) => setCA({ ...ca, monitoring_90_day: e.target.value })}
                      placeholder="What to verify after 90 days." data-testid="monitor-90" />
                  </div>
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-1">Actions taken (one per line , summary bullets)</label>
                <textarea rows={3} className="input" placeholder="e.g. Written warning issued&#10;Mandatory training scheduled"
                  value={actions} onChange={(e) => setActions(e.target.value)} data-testid="findings-actions" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                Confirm the closing communications the investigator has sent. All three should be checked before the case is closed.
              </div>
              {[
                ["reporter_confirmed", "Reporter notified"],
                ["subject_confirmed", "Subject notified"],
                ["witnesses_confirmed", "Witnesses notified"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                  <input type="checkbox" checked={comms[key]}
                    onChange={(e) => setComms({ ...comms, [key]: e.target.checked })}
                    data-testid={`comms-${key}`} />
                  {label}
                </label>
              ))}
              <div>
                <label className="eyebrow block mb-1">Closure date</label>
                <input type="date" className="input !w-auto" value={closureDate} onChange={(e) => setClosureDate(e.target.value)} data-testid="findings-closure-date" />
              </div>
              <div className="border border-amber-300 bg-amber-50 rounded p-3 text-xs text-amber-900">
                {canApproveFindings
                  ? "As Company Admin, these findings will auto-approve on submit."
                  : "These findings will be submitted for Company Admin approval. The case remains open until approved."}
              </div>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-between gap-2">
          <button className="btn-ghost text-sm" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}>Back</button>
          <div className="flex gap-2">
            <button className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
            {step < 4 ? (
              <button className="btn-primary text-sm" onClick={() => setStep(step + 1)} data-testid="findings-wizard-next">Next</button>
            ) : (
              <button className="btn-primary text-sm" onClick={submit} disabled={busy || !summary.trim()} data-testid="findings-wizard-submit">
                <ShieldCheck size={14} strokeWidth={2}/> {busy ? "Submitting..." : (canApproveFindings ? "Issue findings" : "Submit for approval")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
   FINDINGS RIBBON , top-of-page stamp when findings exist
   ============================================================================ */

function FindingsRibbon({ findings, onClickTab }) {
  const outcome = findings.outcome || "findings";
  const status = findings.status || "pending_approval";
  const isFinal = status === "approved";
  const isRejected = status === "rejected";
  const isPending = status === "pending_approval";

  const label = outcome.replace(/_/g, " ").toUpperCase();
  const tint =
    isRejected ? "from-rose-50 to-white border-rose-300 text-rose-900"
    : outcome === "substantiated" ? "from-rose-50 to-white border-rose-300 text-rose-900"
    : outcome === "unsubstantiated" ? "from-emerald-50 to-white border-emerald-300 text-emerald-900"
    : outcome === "partially_substantiated" ? "from-amber-50 to-white border-amber-300 text-amber-900"
    : "from-slate-100 to-white border-slate-300 text-slate-900";

  const stampBorder =
    isRejected ? "border-rose-500 text-rose-700"
    : outcome === "substantiated" ? "border-rose-500 text-rose-700"
    : outcome === "unsubstantiated" ? "border-emerald-600 text-emerald-700"
    : outcome === "partially_substantiated" ? "border-amber-500 text-amber-700"
    : "border-slate-600 text-slate-700";

  return (
    <motion.button
      onClick={onClickTab}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className={"relative w-full mb-5 border-l-4 border bg-gradient-to-r " + tint + " overflow-hidden text-left group"}
      data-testid="findings-ribbon"
      title="Open Findings tab"
    >
      {/* diagonal watermark */}
      <div className="absolute inset-y-0 right-0 w-2/3 pointer-events-none opacity-40 [background:repeating-linear-gradient(-45deg,transparent_0_10px,rgba(0,0,0,0.03)_10px_20px)]"/>
      <div className="relative px-6 py-4 flex items-center gap-5 flex-wrap">
        {/* Stamp */}
        <div className={"shrink-0 border-2 px-3 py-2 -rotate-3 bg-white/70 font-mono uppercase text-[13px] tracking-widest font-bold shadow-sm " + stampBorder}>
          {isRejected ? "REJECTED" : isPending ? "PENDING APPROVAL" : "FINDINGS ISSUED"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-0.5">Case findings</div>
          <div className="font-display text-lg font-semibold truncate">{label}</div>
          <div className="text-xs opacity-70 truncate">
            Submitted by {findings.submitted_by_name}
            {findings.submitted_at && " · " + new Date(findings.submitted_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
            {isFinal && findings.approved_by_name && " · Approved by " + findings.approved_by_name}
          </div>
        </div>
        <div className="shrink-0 hidden sm:flex items-center gap-2">
          {isPending && (
            <span className="pill pill-pending">Awaiting approval</span>
          )}
          {isFinal && (
            <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest">
              <CheckCircle2 size={12} strokeWidth={2}/> Finalized
            </span>
          )}
          {isRejected && (
            <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest">
              <XCircle size={12} strokeWidth={2}/> Rejected
            </span>
          )}
        </div>
      </div>
    </motion.button>
  );
}

function SignalPill({ level }) {
  const map = {
    low: "bg-emerald-50 text-emerald-700 border-emerald-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    high: "bg-rose-50 text-rose-700 border-rose-200",
    critical: "bg-rose-100 text-rose-800 border-rose-300",
  };
  const cls = map[(level || "").toLowerCase()] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span className={"inline-block text-[9px] font-mono uppercase tracking-widest border px-1.5 py-0.5 " + cls}>
      {level || "n/a"}
    </span>
  );
}

/* ============================================================================
   COMMUNICATION STREAM , WhatsApp-style chat with search + read receipts
   ============================================================================ */

const HR_ROLES = ["hr", "company_admin", "super_admin"];

function CommunicationStream({ c, user, onNewCase, canHR }) {
  const [msg, setMsg] = useState("");
  const [internal, setInternal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState([]);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [showSystem, setShowSystem] = useState(true);
  const fileRef = useRef(null);
  const streamEndRef = useRef(null);
  const [ackMessaging, setAckMessaging] = useState(
    () => !!localStorage.getItem("prophr_ack_case_messaging"),
  );

  const canPost = !["withdrawn", "closed", "resolved"].includes(c.status);

  const uploadAttachment = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "file");
        fd.append("reason", "Case communication attachment");
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachmentIds((prev) => [...prev, data.id]);
        setAttachmentPreviews((prev) => [...prev, data]);
      }
    } catch { toast.error("Attachment upload failed"); }
    finally { setUploading(false); }
  };

  const post = async () => {
    if (!msg.trim() && attachmentIds.length === 0) {
      toast.error("Write a message or attach a file first.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/cases/${c.id}/comments`, {
        message: msg.trim(),
        is_internal: internal,
        attachment_ids: attachmentIds,
      });
      onNewCase(data);
      setMsg("");
      setAttachmentIds([]);
      setAttachmentPreviews([]);
      toast.success(internal ? "Internal note added." : "Message posted.");
    } catch { toast.error("Failed to post."); }
    finally { setSending(false); }
  };

  const allEvents = (c.timeline || []).slice().sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));

  // Filter: search + optional hide-system-events
  const filteredEvents = allEvents.filter((ev) => {
    if (!showSystem && ev.kind !== "comment") return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (ev.message || "").toLowerCase().includes(q) ||
      (ev.actor_name || "").toLowerCase().includes(q) ||
      (ev.kind || "").toLowerCase().includes(q)
    );
  });

  // Compute read-receipt state per outbound comment: read if any comment/status_change from another user happened after.
  const lastOtherActivityAt = (() => {
    for (let i = allEvents.length - 1; i >= 0; i--) {
      const e = allEvents[i];
      if (e.actor_id && e.actor_id !== user.id && !e.is_internal) return e.created_at;
    }
    return null;
  })();

  useEffect(() => {
    // Auto-scroll to bottom when new events arrive (only if not searching)
    if (!search.trim() && streamEndRef.current) {
      streamEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [allEvents.length]);

  const commentCount = allEvents.filter((e) => e.kind === "comment").length;

  return (
    <div className="data-cell p-0 overflow-hidden flex flex-col" style={{ minHeight: 560 }}>
      {/* Header */}
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full flex items-center justify-center">
            <MessageSquare size={16} strokeWidth={1.75}/>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Communication stream</div>
            <div className="text-[11px] text-slate-500">{commentCount} messages · {allEvents.length - commentCount} events</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2}/>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search in chat…"
              className="pl-7 pr-2 py-1 text-xs bg-white border border-slate-200 rounded focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 w-52"
              data-testid="chat-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                title="Clear search"
              >
                <X size={11}/>
              </button>
            )}
          </div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1 cursor-pointer" title="Show system events (status changes, workflow, etc.)">
            <input
              type="checkbox"
              checked={showSystem}
              onChange={(e) => setShowSystem(e.target.checked)}
              className="w-3 h-3"
            />
            System
          </label>
        </div>
      </div>

      {/* Stream , WhatsApp-style */}
      <div
        className="flex-1 overflow-y-auto px-4 py-5 space-y-2 bg-slate-50/60"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, rgba(15,23,42,0.03) 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      >
        {search.trim() && (
          <div className="text-center text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">
            {filteredEvents.length} match{filteredEvents.length === 1 ? "" : "es"} for &ldquo;{search}&rdquo;
          </div>
        )}
        {groupByDay(filteredEvents).map(([day, events], gi) => (
          <div key={gi}>
            <div className="flex justify-center py-2 sticky top-0 z-10">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-600 bg-white/90 backdrop-blur border border-slate-200 rounded-full px-3 py-0.5 shadow-sm">
                {day}
              </span>
            </div>
            <div className="space-y-1.5">
              {events.map((ev, i) => (
                <StreamEntry
                  key={ev.id || `${gi}-${i}`}
                  ev={ev}
                  isMine={ev.actor_id === user.id}
                  isHR={HR_ROLES.includes(ev.actor_role || "")}
                  lastOtherActivityAt={lastOtherActivityAt}
                  highlight={search}
                />
              ))}
            </div>
          </div>
        ))}
        {filteredEvents.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-10">
            {search.trim() ? `No messages match "${search}"` : "No activity yet."}
          </div>
        )}
        <div ref={streamEndRef}/>
      </div>

      {/* Composer */}
      {canPost ? (
        <div className="border-t border-slate-200 bg-white p-3">
          {!ackMessaging && (
            <div className="bg-amber-50 border border-amber-300 p-3 mb-3 text-[12px] text-amber-900 flex items-start gap-2 rounded">
              <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" strokeWidth={2}/>
              <div className="flex-1">
                <div className="font-semibold mb-0.5">A note before you post</div>
                Every message here is timestamped, fingerprinted, and retained under your organization&apos;s policy.
                Anything you write may be seen by HR, the assignee, and (for non-internal messages) the reporter.
                <button
                  className="mt-2 btn-ghost text-xs"
                  onClick={() => {
                    localStorage.setItem("prophr_ack_case_messaging", "1");
                    setAckMessaging(true);
                  }}
                  data-testid="case-messaging-ack-btn"
                >I understand</button>
              </div>
            </div>
          )}
          {attachmentPreviews.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {attachmentPreviews.map((a) => (
                <div key={a.id} className="flex items-center gap-2 border border-slate-200 bg-slate-50 px-2 py-1 text-xs rounded">
                  <Paperclip size={11}/> {a.filename}
                  <button
                    onClick={() => {
                      setAttachmentIds((p) => p.filter((x) => x !== a.id));
                      setAttachmentPreviews((p) => p.filter((x) => x.id !== a.id));
                    }}
                    className="text-slate-400 hover:text-red-600 ml-1"
                  >
                    <X size={11}/>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => uploadAttachment(e.target.files)}/>
            <button
              className="p-2 border border-slate-200 hover:border-slate-400 rounded-full transition-colors shrink-0"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              title="Attach a file"
              data-testid="chat-attach-btn"
            >
              <Paperclip size={14} strokeWidth={2} className="text-slate-600"/>
            </button>
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  post();
                }
              }}
              rows={1}
              placeholder={canHR ? "Reply, ask a clarifying question, or Shift+Enter for a new line…" : "Type a message… (Shift+Enter for a new line)"}
              className="flex-1 border border-slate-200 rounded-2xl px-4 py-2 text-sm resize-none focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 min-h-[38px] max-h-32"
              data-testid={TID.caseCommentInput}
            />
            {canHR && (
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1 cursor-pointer shrink-0" data-testid={TID.caseCommentInternal} title="Internal notes are hidden from the reporter">
                <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} className="w-3 h-3"/>
                Internal
              </label>
            )}
            <button
              className={"p-2.5 rounded-full transition-colors shrink-0 " + ((msg.trim() || attachmentIds.length) && !sending ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-slate-200 text-slate-400 cursor-not-allowed")}
              onClick={post}
              disabled={sending || (!msg.trim() && attachmentIds.length === 0)}
              title="Send"
              data-testid={TID.caseCommentSubmit}
            >
              <Send size={14} strokeWidth={2}/>
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-slate-200 bg-slate-100 p-4 text-center text-sm text-slate-600">
          This case is <span className="font-semibold">{c.status}</span>. Messaging is closed.
        </div>
      )}
    </div>
  );
}

function highlightText(text, needle) {
  if (!needle || !needle.trim() || !text) return text;
  const q = needle.trim();
  const parts = String(text).split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
  return parts.map((p, i) =>
    p.toLowerCase() === q.toLowerCase()
      ? <mark key={i} className="bg-yellow-200 text-slate-900 rounded-sm px-0.5">{p}</mark>
      : <span key={i}>{p}</span>,
  );
}

function StreamEntry({ ev, isMine, isHR, lastOtherActivityAt, highlight }) {
  const isSystem = !ev.actor_id;
  const kind = ev.kind || "event";
  const isComment = kind === "comment";
  const isEvidence = kind === "evidence_added";
  const isWorkflow = kind.startsWith("workflow_");
  const isFindings = kind.startsWith("findings");
  const isWithdrawn = kind === "withdrawn";

  // System events render as neutral center-pill row (not chat bubbles)
  if (!isComment) {
    const bg =
      isEvidence ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : isFindings ? "bg-indigo-50 border-indigo-200 text-indigo-900"
      : isWithdrawn ? "bg-rose-50 border-rose-200 text-rose-900"
      : isWorkflow ? "bg-slate-50 border-slate-200 text-slate-700 border-dashed"
      : "bg-white border-slate-200 text-slate-700";
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center py-1">
        <div className={"border px-3 py-1.5 text-[11px] rounded max-w-[85%] " + bg}>
          <span className="font-mono uppercase tracking-widest text-[10px] mr-2 opacity-70">{kind.replaceAll("_", " ")}</span>
          <span className="font-semibold">{ev.actor_name || "System"}</span>
          <span className="mx-1 opacity-70">·</span>
          <span>{highlightText(ev.message || "", highlight)}</span>
          <span className="ml-2 font-mono text-slate-400">{formatTime(ev.created_at)}</span>
          {ev.metadata?.fingerprint && (
            <span className="ml-2 inline-block align-middle"><FingerprintChip fingerprint={ev.metadata.fingerprint}/></span>
          )}
        </div>
      </motion.div>
    );
  }

  // Comment bubbles , WhatsApp style
  const bubbleColor = isMine
    ? "bg-blue-600 text-white border-blue-600 rounded-2xl rounded-br-sm"
    : (ev.is_internal
        ? "bg-amber-50 border-amber-200 text-amber-900 rounded-2xl rounded-bl-sm"
        : "bg-white border-slate-200 text-slate-900 rounded-2xl rounded-bl-sm");

  const roleAccent = !isMine && (isHR ? "text-blue-700" : "text-slate-500");

  // Read receipt logic for my outbound bubble
  const evTime = ev.created_at ? new Date(ev.created_at).getTime() : 0;
  const otherTime = lastOtherActivityAt ? new Date(lastOtherActivityAt).getTime() : 0;
  const isRead = isMine && otherTime > evTime;
  const isDelivered = isMine && !isRead;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={"flex gap-2 " + (isMine ? "flex-row-reverse pl-12" : "pr-12")}
    >
      {/* Avatar */}
      <div className="shrink-0 pt-4">
        <Avatar user={{ name: ev.actor_name || "User" }} size={28} testid="stream-avatar"/>
      </div>

      <div className={"min-w-0 max-w-[78%] " + (isMine ? "items-end flex flex-col" : "")}>
        {!isMine && (
          <div className={"text-[11px] font-semibold mb-0.5 " + (roleAccent || "text-slate-700")}>
            {ev.actor_name || "System"}
            {ev.actor_role && <span className="ml-1.5 font-mono font-normal text-[9px] uppercase tracking-widest text-slate-400">{ev.actor_role.replaceAll("_", " ")}</span>}
            {ev.is_internal && <span className="ml-1.5 text-[9px] font-mono uppercase tracking-widest text-amber-700 bg-amber-100 border border-amber-200 px-1 rounded">Internal</span>}
          </div>
        )}
        <div className={"border px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed shadow-sm " + bubbleColor}>
          <div>{highlightText(ev.message || "(no message)", highlight)}</div>
          {ev.metadata?.attachments?.length > 0 && (
            <div className="mt-2 space-y-1">
              {ev.metadata.attachments.map((a) => (
                <div
                  key={a.id}
                  className={"text-[11px] flex items-center gap-2 border px-2 py-1 rounded " + (isMine ? "bg-white/10 border-blue-400 text-white" : "bg-slate-50 border-slate-200 text-slate-700")}
                >
                  <Paperclip size={10} strokeWidth={2}/>
                  <span className="truncate">{a.filename}</span>
                  {a.fingerprint && <span className="font-mono opacity-60">·{String(a.fingerprint).slice(0, 6)}</span>}
                </div>
              ))}
            </div>
          )}
          <div className={"flex items-center justify-end gap-1 mt-1 text-[10px] font-mono " + (isMine ? "text-blue-100/80" : "text-slate-400")}>
            <span>{formatTime(ev.created_at)}</span>
            {isMine && (
              isRead
                ? <CheckCheck size={11} strokeWidth={2.5} className="text-sky-300" title="Read"/>
                : isDelivered
                  ? <CheckCheck size={11} strokeWidth={2.5} className="text-blue-200/70" title="Delivered"/>
                  : <Check size={11} strokeWidth={2.5} title="Sent"/>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function groupByDay(events) {
  const groups = new Map();
  const today = new Date().toLocaleDateString();
  const yesterday = new Date(Date.now() - 864e5).toLocaleDateString();
  for (const e of events) {
    const d = new Date(e.created_at || Date.now());
    const raw = d.toLocaleDateString();
    let key;
    if (raw === today) key = "Today";
    else if (raw === yesterday) key = "Yesterday";
    else key = d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" });
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  }
  return Array.from(groups.entries());
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/* ============================================================================
   WITHDRAW CASE MODAL
   ============================================================================ */

function WithdrawCaseModal({ caseId, onClose, onWithdrawn }) {
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return toast.error("Please provide a reason.");
    if (!ack) return toast.error("Please acknowledge that withdrawal is final and audited.");
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/withdraw`, {
        reason: reason.trim(),
        acknowledged_final: true,
      });
      onWithdrawn(data);
      toast.success("Case withdrawn. The audit trail preserves the original filing.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdraw failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ y: 12 }} animate={{ y: 0 }} exit={{ y: 12 }}
        className="bg-white border border-slate-300 max-w-lg w-full"
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold text-red-700">Withdraw case</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-300 p-3 text-[13px] text-amber-900 flex gap-2">
            <AlertTriangle size={14} strokeWidth={2} className="text-amber-700 mt-0.5 shrink-0"/>
            <div>
              Withdrawing this case is <span className="font-semibold">final</span>. The case will move to
              a <span className="font-mono">withdrawn</span> state, its active workflow will be canceled, and this
              action will be permanently recorded in the audit trail. Evidence, timeline, and any prior
              findings are preserved and cannot be deleted.
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-1">Reason (required)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="e.g. Resolved directly with my manager, situation improved..."
              className="input"
              data-testid={TID.caseWithdrawReason}
            />
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              className="mt-1"
              data-testid={TID.caseWithdrawAck}
            />
            <span className="text-[13px] text-slate-800">
              I understand this withdrawal is final and will be captured with a timestamp and fingerprint
              in the case audit trail. I am withdrawing of my own free will and without coercion.
            </span>
          </label>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn-danger"
            onClick={submit}
            disabled={busy || !ack || !reason.trim()}
            data-testid={TID.caseWithdrawConfirm}
          >
            <XCircle size={14} strokeWidth={2}/> {busy ? "Withdrawing..." : "Withdraw case"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ============================================================================
   REPORTER PROFILE DRILL-DOWN
   ============================================================================ */

function ReporterProfileModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/users/${userId}/case-stats`)
      .then((r) => setData(r.data))
      .catch(() => setError("Could not load profile."));
  }, [userId]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4"
      data-testid={TID.reporterProfileModal}
    >
      <motion.div
        initial={{ y: 12 }} animate={{ y: 0 }} exit={{ y: 12 }}
        className="bg-white border border-slate-300 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-950 text-slate-100">
          <div>
            <div className="eyebrow text-blue-300">Reporter profile</div>
            <div className="font-editorial text-xl">{data?.user?.name || "Loading..."}</div>
          </div>
          <button onClick={onClose} className="text-slate-300"><X size={18}/></button>
        </div>

        {error && <div className="p-5 text-red-700 text-sm">{error}</div>}

        {data && (
          <div className="p-5 space-y-5">
            {/* Identity card */}
            <div className="grid md:grid-cols-2 gap-4">
              <IdentityRow icon={UserIcon} label="Role" value={data.user.role.replaceAll("_", " ")}/>
              <IdentityRow icon={Briefcase} label="Department" value={data.user.department || "not set"}/>
              <IdentityRow icon={UserIcon} label="Title" value={data.user.title || "not set"}/>
              <IdentityRow icon={MapPin} label="Email" value={data.user.email} mono/>
              <IdentityRow icon={Clock} label="Joined" value={data.user.created_at ? new Date(data.user.created_at).toLocaleDateString() : "unknown"} mono/>
              <IdentityRow icon={Clock} label="Last sign-in" value={data.user.last_login_at ? new Date(data.user.last_login_at).toLocaleString() : "not recorded"} mono/>
            </div>

            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-3">
              <StatTile label="Total cases filed" value={data.total_cases}/>
              <StatTile label="Categories" value={Object.keys(data.by_category).length}/>
              <StatTile
                label="Open now"
                value={(data.by_status.open || 0) + (data.by_status.under_review || 0) + (data.by_status.investigation || 0) + (data.by_status.escalated || 0)}
              />
            </div>

            {/* Breakdown */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="eyebrow mb-2">By status</div>
                <div className="space-y-1">
                  {Object.entries(data.by_status).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm border-b border-slate-100 py-1">
                      <span className="text-slate-700">{k.replaceAll("_", " ")}</span>
                      <span className="font-mono text-slate-900">{v}</span>
                    </div>
                  ))}
                  {Object.keys(data.by_status).length === 0 && <div className="text-sm text-slate-500">No cases yet.</div>}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-2">By category</div>
                <div className="space-y-1">
                  {Object.entries(data.by_category).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm border-b border-slate-100 py-1">
                      <span className="text-slate-700">{k}</span>
                      <span className="font-mono text-slate-900">{v}</span>
                    </div>
                  ))}
                  {Object.keys(data.by_category).length === 0 && <div className="text-sm text-slate-500">No cases yet.</div>}
                </div>
              </div>
            </div>

            {/* Recent cases */}
            <div>
              <div className="eyebrow mb-2">Recent cases (identified only)</div>
              <div className="space-y-2">
                {data.recent_cases.map((c) => (
                  <Link
                    key={c.id}
                    to={`/cases/${c.id}`}
                    className="block border border-slate-200 hover:border-blue-600 p-3 transition-colors"
                    onClick={onClose}
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-mono text-slate-500">{c.case_number}</span>
                      <span className={"pill pill-" + (c.priority || "medium")}>{c.priority}</span>
                      <span className="pill pill-closed">{c.status.replaceAll("_", " ")}</span>
                      <span className="text-[10px] font-mono text-slate-500">{c.category}</span>
                    </div>
                    <div className="text-sm text-slate-900 font-medium truncate">{c.title}</div>
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                      created {new Date(c.created_at).toLocaleDateString()}
                    </div>
                  </Link>
                ))}
                {data.recent_cases.length === 0 && <div className="text-sm text-slate-500">No identified cases on record.</div>}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function IdentityRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="border border-slate-200 p-3">
      <div className="flex items-center gap-2 mb-0.5">
        {Icon && <Icon size={11} strokeWidth={2} className="text-slate-500"/>}
        <span className="eyebrow">{label}</span>
      </div>
      <div className={"text-sm text-slate-900 " + (mono ? "font-mono" : "")}>{value}</div>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="border border-slate-200 p-4 bg-slate-50">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-display text-3xl font-semibold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

/* ============================================================================
   WORKFLOW COMPONENTS
   ============================================================================ */

const STEP_ICONS = {
  pending: "○",
  in_progress: "◐",
  completed: "●",
  skipped: "◌",
};
const STEP_CLASS = {
  pending: "border-slate-300 text-slate-500 bg-white",
  in_progress: "border-blue-600 text-blue-700 bg-blue-50",
  completed: "border-slate-900 text-slate-900 bg-slate-900 !text-white",
  skipped: "border-slate-300 text-slate-400 bg-slate-50",
};

function WorkflowMiniCard({ c, onOpen }) {
  const state = c.workflow_state || [];
  const done = state.filter((s) => s.status === "completed").length;
  const pct = state.length ? Math.round((done / state.length) * 100) : 0;
  const current = state[c.workflow_step_index] || null;
  return (
    <button onClick={onOpen} className="data-cell p-5 text-left w-full hover:border-blue-600 transition-colors" data-testid="workflow-mini-card">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">Workflow</div>
        <span className={"pill " + (c.workflow_status === "completed" ? "pill-approved" : "pill-review")}>
          {(c.workflow_status || "not_started").replaceAll("_", " ")}
        </span>
      </div>
      <div className="text-[13px] font-semibold text-slate-900 truncate">{c.workflow_name || "Workflow"}</div>
      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mt-0.5 mb-3">
        {done} / {state.length} steps · {pct}%
      </div>
      <div className="h-1.5 bg-slate-100 mb-3">
        <div className="h-full bg-blue-600 transition-all" style={{ width: `${pct}%` }}/>
      </div>
      {current && current.status !== "completed" && (
        <div className="text-[11px] text-slate-600">
          <span className="text-slate-400 font-mono uppercase tracking-wider mr-1">Now:</span>
          <span className="font-medium text-slate-900">{current.name}</span>
          <span className="text-slate-400 font-mono ml-1">· {current.assignee_role}</span>
        </div>
      )}
    </button>
  );
}

function WorkflowPanel({ c, user, onChange }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [assignee, setAssignee] = useState("");
  const [users, setUsers] = useState([]);
  const [assigningIdx, setAssigningIdx] = useState(null);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const state = c.workflow_state || [];
  const idx = c.workflow_step_index || 0;
  const current = state[idx];
  const canAct =
    current &&
    (["company_admin", "super_admin"].includes(user.role) ||
      user.role === current.assignee_role ||
      current.assignee_user_id === user.id);

  const advance = async (skip = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${c.id}/workflow/advance`, { note, skip });
      onChange(data);
      setNote("");
      toast.success(skip ? "Step skipped" : "Step advanced");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to advance"); }
    finally { setBusy(false); }
  };

  const assign = async (stepIndex) => {
    if (!assignee) return toast.error("Pick a user first");
    try {
      const { data } = await api.post(`/cases/${c.id}/workflow/assign`, {
        step_index: stepIndex,
        assignee_user_id: assignee,
      });
      onChange(data);
      setAssigningIdx(null);
      setAssignee("");
      toast.success("Step assigned");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to assign"); }
  };

  return (
    <div className="space-y-4" data-testid="workflow-panel">
      <div className="data-cell p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="eyebrow">Workflow</div>
            <div className="font-display text-lg font-semibold text-slate-900 mt-0.5">{c.workflow_name}</div>
          </div>
          <span className={"pill " + (c.workflow_status === "completed" ? "pill-approved" : "pill-review")}>
            {(c.workflow_status || "in_progress").replaceAll("_", " ")}
          </span>
        </div>

        <ol className="space-y-3">
          {state.map((s, i) => {
            const isCurrent = i === idx && c.workflow_status !== "completed";
            const isDone = s.status === "completed";
            const canAssign =
              ["hr", "company_admin", "super_admin"].includes(user.role) && !isDone && !s.assignee_user_id;
            return (
              <li key={s.id} className="relative pl-8" data-testid={`workflow-step-${i}`}>
                <div className={"absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center border text-xs " + STEP_CLASS[s.status]}>
                  {isDone ? "✓" : i + 1}
                </div>
                {i < state.length - 1 && (
                  <div className={"absolute left-3 top-6 bottom-[-12px] w-px " + (isDone ? "bg-slate-900" : "bg-slate-200")}/>
                )}
                <div className={"border p-3 " + (isCurrent ? "border-blue-600 bg-blue-50/40" : "border-slate-200 bg-white")}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{s.name}</span>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{s.status}</span>
                      </div>
                      {s.description && <div className="text-xs text-slate-600 mt-1">{s.description}</div>}
                      <div className="mt-2 text-[11px] font-mono text-slate-500 flex flex-wrap gap-3">
                        <span>Role · {s.assignee_role}</span>
                        {s.assignee_user_name && <span>Assignee · {s.assignee_user_name}</span>}
                        <span>SLA · {s.sla_hours}h</span>
                        {s.due_at && <span>Due · {new Date(s.due_at).toLocaleDateString()}</span>}
                      </div>
                      {s.completed_at && (
                        <div className="text-[11px] text-slate-500 mt-1 italic">
                          Completed by {s.completed_by_name} · {new Date(s.completed_at).toLocaleString()}
                          {s.note && <> , &ldquo;{s.note}&rdquo;</>}
                        </div>
                      )}
                    </div>
                    {canAssign && (
                      <button
                        className="btn-ghost text-xs"
                        onClick={() => setAssigningIdx(assigningIdx === i ? null : i)}
                      >
                        {s.assignee_user_id ? "Reassign" : "Assign"}
                      </button>
                    )}
                  </div>
                  {assigningIdx === i && (
                    <div className="mt-3 pt-3 border-t border-slate-200 flex gap-2 flex-wrap">
                      <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="input !py-1.5 flex-1 min-w-[180px]" data-testid={`workflow-assign-select-${i}`}>
                        <option value="">Pick a user…</option>
                        {users.filter((u) => u.role === s.assignee_role || ["company_admin", "hr"].includes(u.role)).map((u) => (
                          <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
                        ))}
                      </select>
                      <button className="btn-primary text-xs" onClick={() => assign(i)} data-testid={`workflow-assign-confirm-${i}`}>Assign</button>
                      <button className="btn-ghost text-xs" onClick={() => setAssigningIdx(null)}>Cancel</button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {current && c.workflow_status !== "completed" && (
        <div className={"data-cell p-5 " + (canAct ? "border-blue-600" : "")}>
          <div className="eyebrow mb-2">Advance current step</div>
          {canAct ? (
            <>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Optional note about what was done…"
                className="input mb-2"
                data-testid="workflow-advance-note"
              />
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  className="btn-ghost text-xs"
                  onClick={() => advance(true)}
                  disabled={busy}
                  data-testid="workflow-skip-btn"
                >
                  Skip step
                </button>
                <button
                  className="btn-primary"
                  onClick={() => advance(false)}
                  disabled={busy}
                  data-testid="workflow-advance-btn"
                >
                  Mark step complete →
                </button>
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-600">
              This step is assigned to <span className="font-semibold">{current.assignee_user_name || `any ${current.assignee_role.replaceAll("_", " ")}`}</span>. Advance actions are visible only to them or Company Admin.
            </div>
          )}
        </div>
      )}

      {c.workflow_status === "completed" && (
        <div className="border border-green-600 bg-green-50 p-4">
          <div className="flex items-center gap-2 text-green-900 font-semibold">
            <CheckCircle2 size={16} strokeWidth={2}/> Workflow completed
          </div>
        </div>
      )}
    </div>
  );
}
